import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { IconComponent } from '../icon/icon';
import { SelectComponent, type SelectOption } from '../select/select';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { injectActiveTranslation } from '../../services/locale/active-translation';
import { MemberRole } from '../../models';
import { getUserFriendlyErrorMessage } from '../../utils/error.utils';
import { isValidEmail } from '../../utils/validation.utils';

@Component({
  selector: 'app-invite-member-modal',
  imports: [FormsModule, TranslocoModule, ModalComponent, IconComponent, SelectComponent],
  templateUrl: './invite-member-modal.html',
  styleUrl: './invite-member-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteMemberModalComponent {
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly transloco = inject(TranslocoService);
  private readonly activeTranslation = injectActiveTranslation();

  readonly isVisible = input(false);
  readonly organizationId = input<string | null>(null);

  readonly memberInvited = output<void>();
  readonly modalClosed = output<void>();

  // Public so unit tests can manipulate/assert on form state without
  // going through DOM events. Template-only consumers don't care about
  // the access modifier.
  readonly email = signal('');
  readonly role = signal<MemberRole>(MemberRole.MEMBER);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  readonly isFormValid = computed(() => {
    const email = this.email().trim();
    return email.length > 0 && isValidEmail(email) && !!this.role();
  });

  protected readonly roleOptions = computed<readonly SelectOption<MemberRole>[]>(() => {
    this.activeTranslation();
    return [
      { value: MemberRole.ADMIN, label: this.t('role_admin') },
      { value: MemberRole.MEMBER, label: this.t('role_member') },
    ];
  });

  protected readonly modalConfig = computed<ModalConfig>(() => {
    this.activeTranslation();
    return {
      title: this.t('title'),
      size: 'small',
      showLogo: true,
      showCloseButton: true,
      showFooter: true,
      primaryButtonText: this.t('submit'),
      secondaryButtonText: this.transloco.translate('common.cancel'),
    };
  });

  protected readonly primaryButtonDisabled = computed(
    () => this.isLoading() || !this.isFormValid()
  );

  onModalConfirmed(): void {
    if (!this.isFormValid()) {
      this.error.set(this.t('error_invalid'));
      return;
    }
    this.sendInvitation();
  }

  onModalCancelled(): void {
    this.resetForm();
    this.modalClosed.emit();
  }

  onModalClosed(): void {
    this.resetForm();
    this.modalClosed.emit();
  }

  protected clearError(): void {
    this.error.set(null);
  }

  private sendInvitation(): void {
    const orgId = this.organizationId();
    if (!orgId) {
      this.error.set(this.t('error_org_required'));
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.orgManagementApi
      .addOrgMember(orgId, { email: this.email().trim(), role: this.role() })
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.memberInvited.emit();
          this.resetForm();
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          this.error.set(getUserFriendlyErrorMessage(err, this.t('error_failed'), this.transloco));
        },
      });
  }

  private t(key: string): string {
    return this.transloco.translate(`modals.invite_member.${key}`);
  }

  private resetForm(): void {
    this.email.set('');
    this.role.set(MemberRole.MEMBER);
    this.error.set(null);
    this.isLoading.set(false);
  }
}
